import { SelectableItem } from '@disco/types/types/selection'
import DOMPurify from 'dompurify'
import GithubSlugger from 'github-slugger'
import type { User } from 'types/user'

let domPurifyInitialized = false

/**
 * Splits a full name into first and last name components.
 *
 * Handles multiple spaces between names gracefully. Note that this is a simplified
 * approach to name parsing - real-world names can be much more complex.
 * See: https://www.kalzumeus.com/2010/06/17/falsehoods-programmers-believe-about-names/
 *
 * @param name - The full name string to split
 * @returns Tuple containing [firstName, lastName]. If no space is found, returns [name, '']
 *
 * @example
 * ```typescript
 * getFirstLastName('John Doe') // returns ['John', 'Doe']
 * getFirstLastName('John  Doe') // returns ['John', 'Doe'] (handles double spaces)
 * getFirstLastName('John') // returns ['John', '']
 * getFirstLastName('John von Neumann') // returns ['John', 'von Neumann']
 * ```
 */
export const getFirstLastName = (name: string): [string, string] => {
  // almost like split(' '), but better handles double space
  const match = name.match(/^(.+?)\s+(.+)/)
  if (!match) return [name, '']
  const firstname = match[1] || ''
  //
  // not actually last name, but a good enough simplification for DISCO
  //
  // the problem is hard and better be avoided, see why https://www.kalzumeus.com/2010/06/17/falsehoods-programmers-believe-about-names/
  //
  const lastname = match[2] || ''
  return [firstname, lastname]
}

/**
 * Generates initials from a full name.
 *
 * Extracts the first letter of the first name and first letter of the last name.
 * Returns an empty string if an error occurs during processing.
 *
 * @param name - The full name to generate initials from
 * @returns The initials in uppercase, or empty string if processing fails
 *
 * @example
 * ```typescript
 * getInitials('John Doe') // returns 'JD'
 * getInitials('John') // returns 'J'
 * getInitials('') // returns ''
 * ```
 */
export const getInitials = (name: string): string => {
  try {
    const [first, last] = getFirstLastName(name)
    const firstLetter = first[0] || ''
    const lastLetter = last[0] || ''
    return (firstLetter + lastLetter).toUpperCase()
  } catch {
    return ''
  }
}

/**
 * Gets a display name for a user, preferring full name over email.
 *
 * Returns the full name (first + last) if either first_name or last_name is present,
 * otherwise falls back to the email address.
 *
 * @param user - User object with optional first_name, last_name, and email
 * @returns The display name string, or undefined if no email fallback is available
 *
 * @example
 * ```typescript
 * getUserDisplayName({ first_name: 'John', last_name: 'Doe', email: 'john@example.com' })
 * // returns 'John Doe'
 *
 * getUserDisplayName({ first_name: 'John', email: 'john@example.com' })
 * // returns 'John'
 *
 * getUserDisplayName({ email: 'john@example.com' })
 * // returns 'john@example.com'
 * ```
 */
export const getUserDisplayName = (user: Partial<User>): string => {
  const { first_name, last_name, email } = user
  const namePresent =
    (first_name && first_name !== '') || (last_name && last_name !== '')
  return namePresent
    ? `${first_name || ''} ${last_name || ''}`.trim()
    : email || ''
}

/**
 * Capitalizes the first letter of a word.
 *
 * @param word - The word to capitalize
 * @returns The word with the first letter capitalized
 *
 * @example
 * ```typescript
 * camelizeWord('hello') // returns 'Hello'
 * camelizeWord('WORLD') // returns 'WORLD'
 * camelizeWord('') // returns ''
 * ```
 */
export const camelizeWord = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1)

/**
 * Generates a URL-friendly slug from an input string.
 *
 * Uses the GithubSlugger library to create consistent, URL-safe slugs
 * that are compatible with GitHub's slug generation algorithm.
 *
 * @param input - The string to convert to a slug
 * @returns A URL-friendly slug string
 *
 * @example
 * ```typescript
 * getSlug('Hello World!') // returns 'hello-world'
 * getSlug('My Awesome Post #1') // returns 'my-awesome-post-1'
 * ```
 */
export const getSlug = (input: string): string =>
  new GithubSlugger().slug(input)

/**
 * Filters a string to contain only valid float number characters.
 *
 * Removes all non-numeric characters except commas and periods, normalizes
 * decimal separators, and limits to 2 decimal places.
 *
 * @param value - The string to filter
 * @returns String containing only valid float characters
 *
 * @example
 * ```typescript
 * floatOnly('abc123.45def') // returns '123.45'
 * floatOnly('123,45') // returns '123.45'
 * floatOnly('123..45') // returns '123.45'
 * floatOnly('123.456') // returns '123.45'
 * ```
 */
export const floatOnly = (value: string): string => {
  /* eslint-disable no-useless-escape */
  return value
    .replace(/[^\d\,\.]/g, '')
    .replace(/\,/, '.')
    .replace(/\.\./g, '.')
    .replace(/([\.\,]\d{2})(.+)/g, '$1')
  /* eslint-enable no-useless-escape */
}

/**
 * Filters a string to contain only integer digits.
 *
 * @param value - The string to filter
 * @returns String containing only numeric digits
 *
 * @example
 * ```typescript
 * integerOnly('abc123def') // returns '123'
 * integerOnly('12.34') // returns '1234'
 * ```
 */
export const integerOnly = (value: string): string => value.replace(/\D/g, '')

/**
 * Extracts and returns only the first 4 digits from a string (useful for years).
 *
 * @param value - The string to extract year from
 * @returns String containing up to 4 digits
 *
 * @example
 * ```typescript
 * yearOnly('abc2023def') // returns '2023'
 * yearOnly('20231') // returns '2023'
 * yearOnly('23') // returns '23'
 * ```
 */
export const yearOnly = (value: string): string =>
  integerOnly(value).substring(0, 4)

/**
 * Converts a string value to a boolean.
 *
 * Treats 'true' and '1' (case-insensitive) as true, everything else as false.
 *
 * @param value - The string value to convert (can be null or undefined)
 * @returns Boolean representation of the string
 *
 * @example
 * ```typescript
 * stringToBoolean('true') // returns true
 * stringToBoolean('TRUE') // returns true
 * stringToBoolean('1') // returns true
 * stringToBoolean('false') // returns false
 * stringToBoolean('0') // returns false
 * stringToBoolean(null) // returns false
 * ```
 */
export const stringToBoolean = (value?: string | null): boolean => {
  const stringBoolean = String(value || false).toLowerCase()
  return stringBoolean == 'true' || stringBoolean == '1'
}

/**
 * Checks if an album name should be considered unknown/missing.
 *
 * Supports both blank/empty albums and albums explicitly named 'untitled album'.
 *
 * @param album - The album name to check (can be null or undefined)
 * @returns True if the album should be considered unknown
 *
 * @example
 * ```typescript
 * isAlbumUnknown('') // returns true
 * isAlbumUnknown('Untitled Album') // returns true
 * isAlbumUnknown('My Album') // returns false
 * isAlbumUnknown(null) // returns true
 * ```
 */
export const isAlbumUnknown = (album?: string | null): boolean =>
  !album || album.length === 0 || album.toLowerCase() === 'untitled album'

/**
 * Checks if an artist name should be considered unknown/missing.
 *
 * Supports both blank/empty artists and artists explicitly named 'unknown artist'.
 *
 * @param artist - The artist name to check (can be null or undefined)
 * @returns True if the artist should be considered unknown
 *
 * @example
 * ```typescript
 * isArtistUnknown('') // returns true
 * isArtistUnknown('Unknown Artist') // returns true
 * isArtistUnknown('John Doe') // returns false
 * isArtistUnknown(null) // returns true
 * ```
 */
export const isArtistUnknown = (artist?: string | null): boolean =>
  !artist || artist.length === 0 || artist.toLowerCase() === 'unknown artist'

/**
 * Parses a search query string into an array of normalized search terms.
 *
 * Converts to lowercase, trims whitespace, normalizes multiple spaces to single spaces,
 * and splits into individual terms.
 *
 * @param searchTerm - The search query string to parse
 * @returns Array of search terms, or null if input is empty
 *
 * @example
 * ```typescript
 * parseSearchQuery('  Hello   World  ') // returns ['hello', 'world']
 * parseSearchQuery('JavaScript React') // returns ['javascript', 'react']
 * parseSearchQuery('') // returns null
 * parseSearchQuery(null) // returns null
 * ```
 */
export const parseSearchQuery = (
  searchTerm?: string | null,
): string[] | null | undefined => {
  if (!searchTerm || searchTerm.length === 0) return null
  return searchTerm.toLowerCase().trim().replace(/\s+/gi, ' ').split(' ')
}

/**
 * Sanitizes HTML input to prevent XSS attacks using DOMPurify.
 *
 * Automatically sets external links to open in new tabs with security attributes.
 * Optionally removes style attributes and tags for stricter content control.
 *
 * @param input - The HTML string to sanitize
 * @param removeStyle - Whether to remove style attributes and tags (default: false)
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```typescript
 * sanitize('<script>alert("xss")</script><p>Hello</p>') // returns '<p>Hello</p>'
 * sanitize('<a href="http://example.com">Link</a>') // returns '<a href="http://example.com" target="_blank" rel="noreferrer">Link</a>'
 * sanitize('<p style="color: red">Text</p>', true) // returns '<p>Text</p>'
 * ```
 */
export const sanitize = (
  input?: string | null,
  removeStyle?: boolean,
): string => {
  if (!domPurifyInitialized) {
    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
      if ('target' in node) {
        // set all elements owning target to target=_blank
        node.setAttribute('target', '_blank')
        // https://web.dev/external-anchors-use-rel-noopener/
        node.setAttribute('rel', 'noreferrer')
      }
    })
    domPurifyInitialized = true
  }

  const config = removeStyle
    ? {
        FORBID_ATTR: ['style'],
        FORBID_TAGS: ['style'],
      }
    : undefined
  return DOMPurify.sanitize(input ?? '', config)
}

/**
 * Escapes special regex characters in a string to make it safe for use in RegExp.
 *
 * @param value - The string to escape
 * @returns String with regex special characters escaped
 *
 * @example
 * ```typescript
 * escapeRegExp('hello.world') // returns 'hello\\.world'
 * escapeRegExp('$100 (USD)') // returns '\\$100 \\(USD\\)'
 * ```
 */
export const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Creates a pluralized string based on a number.
 *
 * @param num - The number to base pluralization on
 * @param f1 - Singular form string
 * @param f2 - Plural form string
 * @returns Formatted string with number and appropriate singular/plural form
 *
 * @example
 * ```typescript
 * plural(1, 'item', 'items') // returns '1 item'
 * plural(5, 'item', 'items') // returns '5 items'
 * plural(0, 'item', 'items') // returns '0 items'
 * plural(undefined, 'item', 'items') // returns ''
 * ```
 */
export const plural = (num: number, f1: string, f2: string): string => {
  if (num === undefined) return ''
  return `${num} ${num === 1 ? f1 : f2}`
}

/**
 * Generates a label for editing selected items.
 *
 * Creates a descriptive label showing the count and types of selected items
 * for bulk edit operations.
 *
 * @param selectedItems - Array of selected playlists and tracks
 * @returns Formatted label string for edit operations
 *
 * @example
 * ```typescript
 * const items = [
 *   { type: 'track' },
 *   { type: 'track' },
 *   { type: 'playlist' }
 * ]
 * getLabelSelectedItems(items) // returns 'Edit 2 tracks and 1 playlist'
 *
 * const singleItem = [{ type: 'track' }]
 * getLabelSelectedItems(singleItem) // returns 'Edit 1 track'
 * ```
 */
export const getLabelSelectedItems = (
  selectedItems: SelectableItem[],
): string => {
  const items: Record<string, any> = {}
  let label = ''

  selectedItems.forEach(item => {
    items[item?.type] = (items[item?.type] ?? 0) + 1
  })

  Object.keys(items).forEach(key => {
    label += `${label ? ' and ' : ''}${items[key]} ${key}${
      items[key] > 1 ? 's' : ''
    }`
  })

  return `Edit ${label}`
}

/**
 * Formats an array of media items into a descriptive string.
 *
 * Creates human-readable descriptions of media collections, showing up to 2 items
 * by name and indicating if there are additional items. Optionally includes IDs.
 *
 * @param items - Array of media items (tracks, playlists, channels, etc.)
 * @param withId - Optional field name to include IDs in the output
 * @returns Formatted string describing the media items
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { name: 'Song 1', type: 'track', id: 1 },
 *   { name: 'Song 2', type: 'track', id: 2 },
 *   { name: 'Song 3', type: 'track', id: 3 }
 * ]
 *
 * mediaItemsToString(tracks)
 * // 'tracks "Song 1" and "Song 2" and 1 other track'
 *
 * mediaItemsToString(tracks, 'id')
 * // 'tracks "Song 1" (1) and "Song 2" (2) and 1 other track'
 *
 * const channels = [{ name: 'Channel 1', type: 'channel' }]
 * mediaItemsToString(channels)
 * // '"Channel 1"'
 * ```
 */
export const mediaItemsToString = (
  items?: any[] | null,
  withId?: 'playlist_id' | 'id' | null,
): string => {
  if (!items || items.length === 0) return ''

  const type = items[0]?.type || 'channel'
  let postfix = items.length > 1 ? 's ' : ' '

  const shortItems: typeof items = (type ? items.slice(0, 2) : items).filter(
    Boolean,
  )

  const extraTracks = items.length - shortItems.length

  let result = `${type}${postfix}`

  if (type === 'channel') {
    result = ''
  }

  postfix = extraTracks > 1 ? 's' : ''

  result += shortItems
    .map(item => {
      const id = withId && String(item[withId])

      return `"${item.name}"${id ? ' (' + id + ')' : ''}`
    })
    .join(' and ')

  if (extraTracks > 0) {
    result += ` and ${extraTracks} other ${type}${postfix}`
  }

  return result
}

/**
 * Formats an array of strings into a grammatically correct list with "and".
 *
 * Simple alternative to Intl.ListFormat for English language only.
 * Joins multiple items with commas and adds "and" before the last item.
 *
 * @param list - Array of strings to format
 * @returns Formatted list string
 *
 * @example
 * ```typescript
 * formatList(['apple']) // returns 'apple'
 * formatList(['apple', 'banana']) // returns 'apple and banana'
 * formatList(['apple', 'banana', 'cherry']) // returns 'apple, banana and cherry'
 * ```
 */
export const formatList = (list: string[]): string => {
  if (list.length === 1) {
    return list[0] || ''
  }
  const firsts = list.slice(0, list.length - 1)
  const last = list[list.length - 1] || ''
  return firsts.join(', ') + ' and ' + last
}

/**
 * Formats a number in compact notation (e.g., 1K, 1M).
 *
 * Uses Intl.NumberFormat with compact notation when available,
 * falls back to toString() for unsupported browsers (Safari 14.1.2).
 *
 * @param number - The number to format
 * @returns Formatted number string in compact notation
 *
 * @example
 * ```typescript
 * formatNumber(1000) // returns '1K'
 * formatNumber(1500) // returns '1.5K'
 * formatNumber(1000000) // returns '1M'
 * formatNumber(123) // returns '123'
 * ```
 */
export const formatNumber = (number: number): string => {
  let formatter
  try {
    formatter = Intl.NumberFormat('en-US', { notation: 'compact' })
  } catch (err: any) {
    // fallback for Safari 14.1.2
    // TypeError: Failed to initialize NumberFormat since used feature is not supported in the linked ICU version
    console.error('Intl.NumberFormat error', err)
    return number.toString()
  }
  return formatter.format(number)
}

/**
 * Extracts the AWS region from a DISCO S3 URL.
 *
 * Parses URLs containing s3-proxy, disco-data, or disco patterns to extract
 * the region identifier.
 *
 * @param url - The S3 URL to parse
 * @returns The region string, or null if no region is found
 *
 * @example
 * ```typescript
 * getS3RegionFromTheUrl('https://s3-proxy-us-east-1.example.com/file.mp3') // returns 'us-east-1'
 * getS3RegionFromTheUrl('https://disco-data-eu-west-1/file.mp3') // returns 'eu-west-1'
 * getS3RegionFromTheUrl('https://example.com/file.mp3') // returns null
 * ```
 */
export const getS3RegionFromTheUrl = (
  url: string,
): string | null | undefined => {
  const region =
    // eslint-disable-next-line no-useless-escape
    url.match(/(s3-proxy|disco-data|disco)-((\w+|\-)*)(\/|\.)/) || []
  if (region.length < 3) return null
  return region[2]
}

/**
 * Checks if a URL contains watermarking.
 *
 * @param url - The URL to check
 * @returns True if the URL contains '/watermark/', false otherwise
 *
 * @example
 * ```typescript
 * isWatermarkedUrl('https://example.com/watermark/file.mp3') // returns true
 * isWatermarkedUrl('https://example.com/file.mp3') // returns false
 * ```
 */
export const isWatermarkedUrl = (url: string): boolean => {
  return url.includes('/watermark/')
}

/**
 * Adds a period at the end of a string if closing punctuation is missing.
 *
 * Only adds a period if the string doesn't already end with '.', '!', or '?'.
 *
 * @param text - The text to potentially add a period to
 * @returns Text with a period added if needed
 *
 * @example
 * ```typescript
 * maybeAddPeriod('Hello world') // returns 'Hello world.'
 * maybeAddPeriod('Hello world!') // returns 'Hello world!'
 * maybeAddPeriod('Hello world.') // returns 'Hello world.'
 * maybeAddPeriod('Are you sure?') // returns 'Are you sure?'
 * ```
 */
export const maybeAddPeriod = (text: string): string => {
  if (text.endsWith('.') || text.endsWith('!') || text.endsWith('?')) {
    return text
  } else {
    return text + '.'
  }
}

/**
 * Normalizes a string by converting to lowercase, trimming, and removing diacritics.
 *
 * Uses Unicode normalization (NFD) and removes diacritical marks for better
 * text comparison and searching.
 *
 * @param string - The string to normalize
 * @returns Normalized string without diacritics
 *
 * @example
 * ```typescript
 * normalizeString('  Café  ') // returns 'cafe'
 * normalizeString('Naïve') // returns 'naive'
 * normalizeString('Résumé') // returns 'resume'
 * ```
 */
export const normalizeString = (string: string): string => {
  return string
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param s - The string to capitalize
 * @returns String with the first letter capitalized
 *
 * @example
 * ```typescript
 * capitalizeFirstLetter('hello') // returns 'Hello'
 * capitalizeFirstLetter('WORLD') // returns 'WORLD'
 * capitalizeFirstLetter('') // returns ''
 * ```
 */
export const capitalizeFirstLetter = (s: string): string =>
  s.length > 0 ? (s[0]?.toUpperCase() || '') + s.slice(1) : s
